import React, { useEffect, useState } from "react";
import Quiz from '../quiz'
import Loader from "../../components/Loader";
import { CourseTreeView } from './course/Overview';

import { assessments, getGradingModel } from "../../assets/data/assessments";
import { IR_LEVEL_MAPPING } from "../../store/quiz/quizBaseApi";
import { getProfileInfo } from "../../store/profile/profileBaseAPi";
import callApi from "../../utils/api";
import { useStudy } from "./hooks";
import { getById, getFirstCourseAtLevel } from "../../store/course/courseBaseApi";
import { APPLICATION_NAME, nowISO } from "../../utils";
import { DiagnosticTestV2 } from "../diagnosticTestV2/DiagnosticTest";
import { getActivities, getAssessmentSummary, getCourseAssignments } from "../../store/study/studyBaseApi";
import { useIRGrade } from "../irGrading/hooks";
import testDetails from '../../assets/data/diagnostic-test-level-1.json';
import { CourseWelcome } from "./course/Welcome";
import { useDispatch } from "react-redux";
import { sessionTimingAction } from "../../store/sessionTiming/sessionTimingSlice";

export const Study = () => {
    const [assessment, setAssessment] = React.useState({});
    const [assignedCourses, setAssignedCourses] = useState([]); // Store all assigned courses
    // const [selectedCourse, setSelectedCourse] = useState(false); // Track selected course
    const [course, setAssignedCourse] = React.useState({});
    const [summaries, setSummaries] = React.useState([]);
    const [activities, setActivities] = React.useState([]);
    const [testDetail, setTestDetail] = React.useState({});
    const userDetails = React.useRef(JSON.parse(localStorage.getItem('user') || null));
    const { loadGradeMap, gradingList } = useIRGrade();
    const [grading, setGrading] = useState();
    const dispatch = useDispatch();

    // const profile = React.useRef(JSON.parse(localStorage.getItem('profile')) || {student: {}});

    const { setIRLevelMapping, profile, assignCourse } = useStudy();
    // True when the student's gradeLevel is set but the admin hasn't authored
    // a course of their URBAN/RURAL type at that level yet. Shown as a
    // "course being prepared" screen instead of re-running the diagnostic.
    const [awaitingCourse, setAwaitingCourse] = useState(false);
    const [awaitingLevel, setAwaitingLevel] = useState(null);

    useEffect(() => {
        loadGradeMap();
    }, [])

    const getAssessment = async (grade) => {
        let api = callApi();

        let mappings = await api.asyncFetchContent(IR_LEVEL_MAPPING);

        let detail = mappings.find(e => e.gradeId == grade) || mappings.find(e => e.irLevels.includes(grade));
        if (detail) {
            setIRLevelMapping(detail);
            let url = detail.diagnosticAssessmentUrl;
            return await api.asyncFetchContent(url);
        }
    }

    React.useEffect(() => {
        let grade = 'Grade 1', student = {};

        if (!profile.id) return;

        (async () => {
            try {
                let { data } = await getProfileInfo({
                    payload: { username: userDetails.current.userName, roles: userDetails.current.roles[0].role }
                });

                if (data && data.student) {
                    student = data.student;
                    data.student.grade && (grade = data.student.grade)
                }

            } catch (e) {
                console.error(e)
            }

            let assignedCourse = {};
            try {
                let assignmentResp = await getCourseAssignments({ userId: profile.id });
                assignedCourse = assignmentResp?.data;
                let assignedCourses = assignmentResp?.data?.courses || [];

                setAssignedCourses(assignedCourses);
            } catch (e) { }

            if (assignedCourse && assignedCourse.courses && assignedCourse.courses.length) {
                let _course = assignedCourse.courses[assignedCourse.courses.length - 1]; // Take the latest one

                /**
                 * @description Hardcoding course for testing purposes, remove the variable re-initialization once testing is done
                 */
                // _course = {
                //     courseId: "2c9f9a8f9055277d01905845d4c80003"
                // }

                setAssignedCourse(_course);

                if (Object.keys(student).length) {
                    let { courseId } = _course;
                    try {
                        let [activities, summaries] = await Promise.all([
                            getActivities({ params: { courseId, assigneeId: student.id } }),
                            getAssessmentSummary({ params: { courseId, assigneeId: student.id } }),
                        ]);

                        activities = activities?.data?.activities || [];
                        summaries = summaries?.data?.progressSummary || [];

                        setSummaries(summaries);
                        setActivities(activities);

                        // Recover session timer from backend if localStorage was cleared
                        const existingEndTime = localStorage.getItem("sessionEndTime");
                        const hasActiveLocalSession = existingEndTime && Number(existingEndTime) > Date.now();
                        if (!hasActiveLocalSession && activities.length) {
                            const sessionActivities = activities
                                .filter(a => a.type === "SESSION_START" && a.endTime)
                                .sort((a, b) => Number(b.startTime) - Number(a.startTime));
                            if (sessionActivities.length > 0) {
                                const latestEnd = Number(sessionActivities[0].endTime);
                                if (latestEnd > Date.now()) {
                                    const remainingSeconds = Math.floor((latestEnd - Date.now()) / 1000);
                                    dispatch(sessionTimingAction.setSessionTime(remainingSeconds));
                                    dispatch(sessionTimingAction.startSessionTimer());
                                }
                            }
                        }
                    } catch (e) { console.log(e) }
                }

                return;
            }

            // Student already has an IR level assigned (from a previous
            // diagnostic) but no courses. Don't re-run the diagnostic — instead
            // try to resolve a course for that level. This covers the case
            // where the admin authored the missing URBAN/RURAL course AFTER the
            // student took the diagnostic.
            if (student?.gradeLevel && student?.id) {
                try {
                    console.log(
                        "[Study] student has gradeLevel but no courses — trying /level-course",
                        { studentId: student.id, gradeLevel: student.gradeLevel },
                    );
                    const { data } = await getFirstCourseAtLevel({
                        studentId: student.id,
                        level: student.gradeLevel,
                    });
                    console.log("[Study] /level-course response", data);
                    if (data?.nextLevel?.irCourseId) {
                        // A course is now available — assign it and reload so
                        // the normal "student has courses" branch picks up.
                        const courseId = data.nextLevel.irCourseId;
                        const courseResp = await getById({ id: courseId });
                        const courseData = courseResp?.data;
                        if (courseData) {
                            const now = nowISO();
                            assignCourse({
                                body: {
                                    application: APPLICATION_NAME,
                                    assigneeRole: "student",
                                    assignedUserId: student.id,
                                    courses: [{
                                        courseId: courseData.id,
                                        courseName: courseData.courseName,
                                        courseCode: courseData.code,
                                        dateEnrolled: now,
                                        assignedBy: student.id,
                                        assignedDate: now,
                                        assignedMethod: "",
                                        currentStatus: "ASSIGNED",
                                    }],
                                },
                            });
                            console.log(
                                "[Study] ✅ auto-assigned course",
                                courseId,
                                "at level",
                                student.gradeLevel,
                                "— reloading",
                            );
                            // Give the saga a tick to fire the PUT, then reload.
                            setTimeout(() => window.location.reload(), 500);
                            return;
                        }
                    }
                    if (data?.noCourseForStudentType) {
                        console.warn(
                            "[Study] no course of student's type at level",
                            student.gradeLevel,
                            "— showing waiting screen",
                        );
                        setAwaitingLevel(student.gradeLevel);
                        setAwaitingCourse(true);
                        return;
                    }
                } catch (e) {
                    console.error("[Study] /level-course retry failed", e);
                    // Fall through to diagnostic.
                }
            }

            if (grade) {
                // let _assessment = await getAssessment(grade);
                // if (!_assessment) {
                //     return
                // }
                // _assessment.gradingModel = getGradingModel(_assessment.gradingModel);
                // setAssessment(_assessment);

                let detail2 = testDetails.find(item => Boolean(item.applicableGrades.find(e => e.grade == grade)))

                // Resolve the student's geographical_type (Urban/Rural).
                // Default to URBAN when missing so legacy / unset students
                // still see content.
                const rawType = student?.geographicalType;
                const studentType = (rawType || "URBAN")
                    .toString()
                    .trim()
                    .toUpperCase();

                // Prefer the irGrading row matching BOTH the student's grade
                // and their studentType. If no typed row exists for this grade,
                // fall back to any row matching the grade (legacy safety).
                let detail = gradingList.find(item =>
                    (item.studentType || "URBAN").toUpperCase() === studentType &&
                    item.diagnosticOne?.applicableGrades.some(e => e.grade === grade)
                );
                let fallback = false;
                if (!detail) {
                    fallback = true;
                    detail = gradingList.find(item =>
                        item.diagnosticOne?.applicableGrades.some(e => e.grade === grade)
                    );
                }
                console.log("[Diagnostic picker]", {
                    grade,
                    rawGeographicalType: rawType,
                    resolvedStudentType: studentType,
                    fallbackUsed: fallback,
                    matchedIrGradingId: detail?.irGradingId,
                    matchedStudentType: detail?.studentType,
                    matchedCourseId: detail?.diagnosticOne?.courseId,
                    totalRows: gradingList?.length,
                });
                setTestDetail(detail);
            }
        })();

    }, [profile, gradingList]);


    if (awaitingCourse) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "60vh",
                    padding: "2rem",
                    textAlign: "center",
                }}
            >
                <h2 style={{ color: "var(--mui-palette-primary-blue, #2e3b6e)", marginBottom: "1rem" }}>
                    Your course is being prepared
                </h2>
                <p style={{ color: "#555", maxWidth: 520, marginBottom: "1.5rem" }}>
                    You're on level <strong>{awaitingLevel}</strong>. We're
                    setting up the right course for you — please check back
                    soon.
                </p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        background: "linear-gradient(180deg, #ffc700 0%, #ff8a00 100%)",
                        color: "#fff",
                        fontWeight: 700,
                        border: "none",
                        borderRadius: 999,
                        padding: "10px 28px",
                        fontSize: "0.95em",
                        cursor: "pointer",
                        fontFamily: '"Quicksand", sans-serif',
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                >
                    Continue →
                </button>
            </div>
        );
    }

    // if (Object.keys(testDetail).length) {
    if (gradingList && testDetail?.irGradingId) {
        return <DiagnosticTestV2 testDetail={testDetail} />
        // } else if (Object.keys(course).length) {
        //     return <CourseTreeView courseId={course.courseId} status={course.currentStatus} summaries={summaries} activities={activities} />
        // } else {
        //     return <Loader />
        // }
    } else if (Object.keys(course).length) {
        // if (!selectedCourse) {
        //     return (<CourseWelcome student={profile} setSelectedCourse={setSelectedCourse} courseId={course.courseId} activities={activities} />
        //     );
        // } else {
        return (
            <CourseTreeView courseId={course.courseId} status={course.currentStatus} summaries={summaries} activities={activities} />
            //     return <CourseTreeView courseId={course.courseId} status={course.currentStatus} summaries={summaries} activities={activities} />
            // } else {
            //     return <Loader />
            // }
            // return (
            // <CourseTreeView
            //     courseId={selectedCourse.courseId}
            //     status={selectedCourse.currentStatus}
            //     summaries={summaries}
            //     activities={activities}
            // />
        );
        // }
    } else {
        return <Loader />
    }
}